import { useQuery } from '@tanstack/react-query';

import { axiosPrivate, formatCurrency } from 'app/utils';

// --- Types ---

export type BillingCycle = 'annual' | 'monthly';

export interface SubscriptionPricing {
  monthly: number;
  annual: number;
  currency: string;
  monthlyFormatted: string;
  annualFormatted: string;
}

export const DEPLOYMENT_FEE = 399;
export const DEPLOYMENT_FEE_CURRENCY = 'GBP';

interface OrganisationPlansResponse {
  plus: boolean;
  bespoke: boolean;
}

type PriceFrequency = 'annually' | 'monthly' | 'biweekly' | 'weekly';

interface PlanPriceResponse {
  id: string;
  value: number;
  currency: string;
  frequency: PriceFrequency;
}

interface BrowsePlanPricesResponse {
  prices: PlanPriceResponse[];
}

// --- Fetch ---

function fetchOrganisationPlans(organisationId: string): Promise<OrganisationPlansResponse> {
  return axiosPrivate
    .get<OrganisationPlansResponse>(`/v3/pharmacist/organisations/${organisationId}/plans`)
    .then((r) => r.data);
}

function fetchWebsitePlanPrices(): Promise<BrowsePlanPricesResponse> {
  return axiosPrivate
    .get<BrowsePlanPricesResponse>('/v3/pharmacist/plans/plus-website/prices')
    .then((r) => r.data);
}

// --- Query options ---

interface PlanQueryOptions {
  enabled?: boolean;
}

function getOrganisationPlansQueryOptions(
  organisationId: string,
  { enabled = true }: PlanQueryOptions = {}
) {
  return {
    queryKey: ['organisation-plans', organisationId],
    queryFn: () => fetchOrganisationPlans(organisationId),
    enabled: !!organisationId && enabled,
    staleTime: 5 * 60 * 1000,
    meta: { errorMessage: 'Failed to load subscription plans' },
  };
}

function getWebsitePlanPricingQueryOptions() {
  return {
    queryKey: ['website-plan-pricing'],
    queryFn: fetchWebsitePlanPrices,
    staleTime: 10 * 60 * 1000,
    meta: { errorMessage: 'Failed to load pricing' },
  };
}

// --- Selectors ---

function selectPricing(data: BrowsePlanPricesResponse): SubscriptionPricing {
  const monthlyPrice = data.prices.find((p) => p.frequency === 'monthly');
  const annualPrice = data.prices.find((p) => p.frequency === 'annually');

  if (!monthlyPrice || !annualPrice) {
    throw new Error('Plan is missing monthly or annual pricing');
  }

  const { value: monthly, currency } = monthlyPrice;
  const { value: annual } = annualPrice;

  return {
    monthly,
    annual,
    currency,
    monthlyFormatted: formatCurrency(monthly, currency) ?? '',
    annualFormatted: formatCurrency(annual, currency) ?? '',
  };
}

// --- Hooks ---

export function useOrganisationPlans(organisationId: string) {
  return useQuery(getOrganisationPlansQueryOptions(organisationId));
}

export function useHasActiveWebsitePlan(organisationId: string, options?: PlanQueryOptions) {
  return useQuery({
    ...getOrganisationPlansQueryOptions(organisationId, options),
    select: (data): boolean => data.plus || data.bespoke,
  });
}

export function useHasBespokePlan(organisationId: string, options?: PlanQueryOptions) {
  return useQuery({
    ...getOrganisationPlansQueryOptions(organisationId, options),
    select: (data): boolean => data.bespoke,
  });
}

export function useSubscriptionPricing() {
  return useQuery({
    ...getWebsitePlanPricingQueryOptions(),
    select: selectPricing,
  });
}
